/**
 * The point-of-collection privacy notice that sits inside the signup form
 * (FR-016, contracts/web-surface.md §3). It is the site's entire privacy
 * disclosure — there is no policy page to link to — so it has to be legible
 * where the address is typed rather than one navigation away.
 *
 * ## Persistently visible by construction
 *
 * FR-016 and SC-010 require the notice to be readable with **no interaction**
 * at both 375 px and 1280 px: no hover, no "learn more" expander, no extra
 * navigation. The way to guarantee that is to build nothing here that could
 * hide it — no disclosure state, no `numberOfLines` truncation, no
 * width-conditional branch, no press target. This renders one paragraph of
 * ordinary body text and offers no way to collapse it, so there is no code path
 * on which the idle form is missing it.
 *
 * There is deliberately no consent checkbox either: submitting the form is
 * itself the affirmative act, so the notice informs and never gates.
 *
 * Layout belongs to the caller. The form (T045) places this above the submit
 * control and owns the column the text wraps inside; a width or a margin here
 * would only fight that.
 *
 * ## Where the contact route comes from
 *
 * `buildPrivacyNotice` is pure and unit-tested, and stays that way: the contact
 * route is read from the environment **here**, at the call site. Vite
 * substitutes `import.meta.env.VITE_REMOVAL_CONTACT` at build time, so by the
 * time the page runs the notice is a constant — which is why it is built once
 * at module scope instead of on every render.
 *
 * When the variable is unset the builder drops the contact clause whole and the
 * removal sentence still finishes as a sentence. The result is plain prose,
 * never a `mailto:` link: an address that linkifies when configured and dangles
 * when it is not would be worse than one the visitor selects and copies, which
 * the browser already allows here.
 */

import { Text } from "react-native-paper";
import { buildPrivacyNotice } from "@/lib/privacy-notice";
import { useAppTheme } from "@/theme/paper-theme";

/**
 * Annotated at the boundary because `import.meta.env` is loosely typed, and the
 * builder's unconfigured branch is only honest if `undefined` can genuinely
 * arrive here.
 */
const removalContact: string | undefined = import.meta.env.VITE_REMOVAL_CONTACT;

const NOTICE = buildPrivacyNotice(removalContact);

export function PrivacyNotice() {
  const theme = useAppTheme();

  return (
    <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
      {NOTICE}
    </Text>
  );
}
